
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { useToast } from "@/hooks/use-toast";

export function useFileCompletion(resourceId?: string, fileId?: string) {
  const { session } = useSession();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isFirebaseError, setIsFirebaseError] = useState(false);

  const checkCompletion = useCallback(async () => {
    if (!resourceId || !fileId || !session?.user?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      setIsFirebaseError(false);
      
      console.log(`Checking completion for file ${fileId} in resource ${resourceId}`);
      
      const { data: progressData, error: progressError } = await supabase
        .from('training_progress')
        .select('completed, completed_at')
        .eq('resource_id', resourceId)
        .eq('file_id', fileId)
        .eq('user_id', session.user.id)
        .maybeSingle();
        
      if (progressError) {
        console.error('Error checking completion status:', progressError);
        setError(progressError);
        return;
      }
      
      console.log('Completion data:', progressData);
      
      if (progressData?.completed) {
        setIsCompleted(true);
        setCompletedAt(progressData.completed_at || null);
        console.log('Training is completed for this file');
        return;
      }
      
      const { data: fileData, error: fileError } = await supabase
        .from('resource_files')
        .select('file_type')
        .eq('id', fileId)
        .maybeSingle();
        
      if (fileError) {
        console.error('Error checking file type:', fileError);
        setError(fileError);
        return;
      }
      
      if (fileData) {
        if (fileData.file_type === 'free_response') {
          const { data: responseData, error: responseError } = await supabase
            .from('free_response_attempts')
            .select('completed_at')
            .eq('file_id', fileId)
            .eq('trainee_id', session.user.id)
            .not('completed_at', 'is', null)
            .maybeSingle();
            
          if (responseError) {
            console.error('Error checking free response completion:', responseError);
          } else if (responseData?.completed_at) {
            setIsCompleted(true);
            setCompletedAt(responseData.completed_at);
            console.log('Free response is completed');
          }
        } else if (fileData.file_type === 'quiz') {
          const { data: quizData, error: quizError } = await supabase
            .from('resource_files')
            .select('quiz_id')
            .eq('id', fileId)
            .maybeSingle();
            
          if (quizError) {
            console.error('Error fetching quiz ID:', quizError);
          } else if (quizData?.quiz_id) {
            const { data: attemptData, error: attemptError } = await supabase
              .from('quiz_attempts')
              .select('completed_at')
              .eq('quiz_id', quizData.quiz_id)
              .eq('trainee_id', session.user.id)
              .eq('passed', true)
              .limit(1);
              
            if (attemptError) {
              console.error('Error checking quiz completion:', attemptError);
            } else if (attemptData && attemptData.length > 0) {
              setIsCompleted(true);
              setCompletedAt(attemptData[0].completed_at);
              console.log('Quiz is completed');
            }
          }
        } else if (fileData.file_type === 'drag_drop') {
          // Check drag_drop_attempts table for completion
          const { data: dragDropData, error: dragDropError } = await supabase
            .from('drag_drop_attempts')
            .select('completed_at')
            .eq('file_id', fileId)
            .eq('trainee_id', session.user.id)
            .eq('passed', true)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (dragDropError) {
            console.error('Error checking drag and drop completion:', dragDropError);
          } else if (dragDropData && dragDropData.length > 0) {
            setIsCompleted(true);
            setCompletedAt(dragDropData[0].completed_at);
            console.log('Drag and drop is completed');
          }
        } else if (fileData.file_type === 'linking') {
          // Check linking_attempts table for completion
          const { data: linkingData, error: linkingError } = await supabase
            .from('linking_attempts')
            .select('completed_at')
            .eq('file_id', fileId)
            .eq('trainee_id', session.user.id)
            .eq('passed', true)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (linkingError) {
            console.error('Error checking linking game completion:', linkingError);
          } else if (linkingData && linkingData.length > 0) {
            setIsCompleted(true);
            setCompletedAt(linkingData[0].completed_at);
            console.log('Linking game is completed');
          }
        }
      }
    } catch (err) {
      console.error('Error in checkCompletion:', err);
      
      const errorString = String(err);
      if (
        errorString.includes('firebase') || 
        errorString.includes('firestore') || 
        errorString.includes('400') || 
        errorString.includes('Failed to fetch')
      ) {
        setIsFirebaseError(true);
      }
      
      setError(err instanceof Error ? err : new Error('Unknown error in checkCompletion'));
    } finally {
      setIsLoading(false);
    }
  }, [resourceId, fileId, session?.user?.id]);

  return {
    isLoading,
    isCompleted,
    completedAt,
    error,
    isFirebaseError,
    checkCompletion
  };
}
